/* Bottom threshold bar: mount + live spend-threshold updater
   - Mounts the sticky bar (show + body padding + dismiss memory)
   - Countdown timer
   - Cart-driven threshold updates, per cart currency (USD, CAD, GBP, AUD, EUR)
   - Any currency without a threshold shows the fallback text instead
   - Updates both the sticky bar and the cart drawer threshold UI
*/

(function () {
  const root =
    document.querySelector('#bottom-sticky-bar') ||
    document.querySelector('[data-sticky-bar]');
  if (!root) return;

  // Selectors. The legacy data-bfcm-* hooks are kept so the cart drawer keeps
  // updating until snippets/cart-drawer.liquid is renamed.
  const TEXT_SEL = '[data-sticky-bar-text], [data-bfcm-text]';
  const FILL_SEL =
    '.sticky-bar-fill, [data-sticky-bar-fill], .bfcm-fill, [data-bfcm-fill]';

  // ====== MOUNT / SHOW ======================================================
  function mountBottomBar() {
    const remember = root.getAttribute('data-remember') === 'true';
    const key = 'bottomThresholdBarDismissed';

    if (remember) {
      try {
        const v = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (v && Date.now() < v.until) return;
      } catch (e) {}
    }

    function applyPadding() {
      const h = root.getBoundingClientRect().height || 56;
      document.body.style.paddingBottom =
        'calc(' + h + 'px + env(safe-area-inset-bottom))';
    }

    root.style.display = 'block';
    applyPadding();

    const closeBtn = root.querySelector('.sticky-bar-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        root.style.display = 'none';
        document.body.style.paddingBottom = '';

        if (remember) {
          const hours = parseInt(root.getAttribute('data-remember-hours') || '12', 10);
          try {
            sessionStorage.setItem(
              key,
              JSON.stringify({ until: Date.now() + hours * 60 * 60 * 1000 })
            );
          } catch (e) {}
        }
      });
    }

    window.addEventListener('resize', applyPadding);
  }

  // ====== COUNTDOWN TIMER ===================================================
  function initCountdown() {
    const timer = root.querySelector('#sticky-bar-timer');
    if (!timer) return;

    const year = parseInt(timer.dataset.year, 10);
    const month = parseInt(timer.dataset.month, 10);
    const day = parseInt(timer.dataset.day, 10);
    const hour = parseInt(timer.dataset.hour, 10);
    const minute = parseInt(timer.dataset.minute, 10);

    if (!year || !month || !day) return;

    const showDays = timer.dataset.showDays === 'true';
    const showHours = timer.dataset.showHours === 'true';
    const showMinutes = timer.dataset.showMinutes === 'true';
    const showSeconds = timer.dataset.showSeconds === 'true';

    const endLabel = root.getAttribute('data-end-label') || 'Offer has ended';
    const labelEl = root.querySelector('[data-sticky-bar-label]');

    // End time is read as Toronto time. -04:00 is daylight time (until Nov 1, 2026);
    // change to -05:00 for an end date after the clocks change.
    const targetTs = new Date(
      `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(
        2,
        '0'
      )}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00-04:00`
    ).getTime();

    const daysCol = timer.querySelector('[data-unit="days"]');
    const hoursCol = timer.querySelector('[data-unit="hours"]');
    const minutesCol = timer.querySelector('[data-unit="minutes"]');
    const secondsCol = timer.querySelector('[data-unit="seconds"]');

    const daysNum = timer.querySelector('[data-sticky-bar-days]');
    const hoursNum = timer.querySelector('[data-sticky-bar-hours]');
    const minutesNum = timer.querySelector('[data-sticky-bar-minutes]');
    const secondsNum = timer.querySelector('[data-sticky-bar-seconds]');

    function setColumn(colEl, numEl, value, show) {
      if (!colEl || !numEl) return;
      if (!show) {
        colEl.style.display = 'none';
        return;
      }
      colEl.style.display = '';
      numEl.textContent = value;
    }

    function updateCountdown() {
      const now = Date.now();
      const diff = targetTs - now;

      if (diff <= 0) {
        clearInterval(interval);
        if (labelEl) labelEl.textContent = endLabel;
        timer.style.display = 'none';
        return;
      }

      let totalSeconds = Math.floor(diff / 1000);
      const days = Math.floor(totalSeconds / (60 * 60 * 24));
      totalSeconds -= days * 60 * 60 * 24;
      const hours = Math.floor(totalSeconds / (60 * 60));
      totalSeconds -= hours * 60 * 60;
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds - minutes * 60;

      setColumn(daysCol, daysNum, String(days), showDays);
      setColumn(hoursCol, hoursNum, String(hours).padStart(2, '0'), showHours);
      setColumn(minutesCol, minutesNum, String(minutes).padStart(2, '0'), showMinutes);
      setColumn(secondsCol, secondsNum, String(seconds).padStart(2, '0'), showSeconds);
    }

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
  }

  // ====== THRESHOLD LOGIC ===================================================
  const rightMode = root?.dataset?.rightMode || 'threshold';
  const thresholdNodes = document.querySelectorAll(TEXT_SEL);
  const shouldRunThreshold = rightMode === 'threshold' && thresholdNodes.length > 0;

  const EXCLUDE_TITLES = ['TSR Package Protection'];

  // Thresholds are entered in the section settings, one per cart currency.
  const THRESHOLD_ATTR = {
    USD: 'thresholdUsd',
    CAD: 'thresholdCad',
    GBP: 'thresholdGbp',
    AUD: 'thresholdAud',
    EUR: 'thresholdEur'
  };

  function getCurrency() {
    // The currency the shopper is actually seeing and paying in.
    const active =
      window.Shopify && Shopify.currency && Shopify.currency.active;
    if (active) return String(active).toUpperCase();

    // Fallback if the currency object is missing: infer from country.
    const shopifyCountry = (window.Shopify && Shopify.country) || '';
    const htmlCountry = document.documentElement.getAttribute('data-country') || '';
    const country = (shopifyCountry || htmlCountry || '').toUpperCase();

    if (country === 'US') return 'USD';
    if (country === 'CA') return 'CAD';
    if (country === 'GB' || country === 'UK') return 'GBP';
    if (country === 'AU') return 'AUD';
    return '';
  }

  function moneyFmt(amount, currency) {
    // narrowSymbol gives "$168.00" instead of "A$168.00" / "CA$168.00"
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol'
    }).format(amount);
  }

  function debounce(fn, ms) {
    let t;
    return function () {
      clearTimeout(t);
      const args = arguments;
      const ctx = this;
      t = setTimeout(() => fn.apply(ctx, args), ms);
    };
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', {
      headers: { 'Cache-Control': 'no-cache' }
    });
    if (!res.ok) throw new Error('Cart fetch failed');
    return res.json();
  }

  function computeEligibleSubtotal(cart) {
    let cents = 0;

    for (const it of cart.items) {
      const title = (it.product_title || it.title || '').trim();
      const excludeByTitle = EXCLUDE_TITLES.some((x) => title.indexOf(x) !== -1);
      const isGiftCard =
        !!it.gift_card || (it.product_type || '').toLowerCase() === 'gift card';

      if (excludeByTitle || isGiftCard) continue;

      let lineCents;
      if (typeof it.original_line_price === 'number' && it.original_line_price > 0) {
        lineCents = it.original_line_price;
      } else if (typeof it.original_price === 'number' && it.original_price > 0) {
        lineCents = it.original_price * it.quantity;
      } else {
        lineCents = (it.price ?? it.final_price) * it.quantity;
      }

      cents += lineCents;
    }

    return cents / 100;
  }

  function getThresholdSettings() {
    const currency = getCurrency();

    const attr = THRESHOLD_ATTR[currency];
    const raw = attr ? parseFloat(root.dataset[attr]) : NaN;
    const threshold = isFinite(raw) && raw > 0 ? raw : null;

    return {
      currency,
      threshold,
      fallbackText: root.dataset.fallbackText || '',
      discountLabel: root.dataset.discountLabel || 'OFFER',
      unlockedText: root.dataset.unlockedText || 'OFFER UNLOCKED',
      awayTextTemplate:
        root.dataset.awayTextTemplate || '[[amount]] AWAY FROM [[discount]]'
    };
  }

  function render(subtotal) {
    const {
      currency,
      threshold,
      fallbackText,
      discountLabel,
      unlockedText,
      awayTextTemplate
    } = getThresholdSettings();

    let text = '';
    let pct = 0;

    if (threshold === null) {
      // No threshold for this currency: show the fallback text, no progress.
      text = fallbackText;
    } else if (subtotal >= threshold) {
      text = unlockedText;
      pct = 100;
    } else {
      const diff = Math.max(0, threshold - subtotal);
      text = awayTextTemplate
        .replace('[[amount]]', moneyFmt(diff, currency))
        .replace('[[discount]]', discountLabel);
      pct = Math.max(0, Math.min(100, (subtotal / threshold) * 100));
    }

    document.querySelectorAll(TEXT_SEL).forEach((el) => {
      el.textContent = text;
    });

    document.querySelectorAll(FILL_SEL).forEach((fill) => {
      fill.style.width = `${pct}%`;
    });
  }

  async function updateThreshold(retryCount = 0) {
    if (!shouldRunThreshold) return;

    try {
      const cart = await fetchCart();
      const subtotal = computeEligibleSubtotal(cart);
      render(subtotal);
    } catch (e) {
      console.warn('[Bottom Threshold Bar] update failed:', e);

      if (retryCount < 2) {
        setTimeout(() => updateThreshold(retryCount + 1), 250);
        return;
      }

      document.querySelectorAll(TEXT_SEL).forEach((el) => {
        if (!el.textContent || el.textContent.trim() === 'Loading…') {
          el.textContent = 'Details available in cart';
        }
      });
    }
  }

  const updateNow = () => updateThreshold();
  const updateSoon = debounce(updateThreshold, 120);

  function wireEvents() {
    if (!shouldRunThreshold) return;

    document.addEventListener('cart:updated', updateSoon);
    document.addEventListener('cart:change', updateSoon);

    window.Shopify = window.Shopify || {};

    const originalOnItemAdded = Shopify.onItemAdded;
    Shopify.onItemAdded = function () {
      updateNow();
      if (typeof originalOnItemAdded === 'function') {
        originalOnItemAdded.apply(this, arguments);
      }
    };

    const originalOnCartUpdate = Shopify.onCartUpdate;
    Shopify.onCartUpdate = function () {
      updateSoon();
      if (typeof originalOnCartUpdate === 'function') {
        originalOnCartUpdate.apply(this, arguments);
      }
    };

    document.body.addEventListener(
      'submit',
      (e) => {
        const form = e.target.closest('form[action*="/cart/add"]');
        if (!form) return;
        setTimeout(updateSoon, 150);
        setTimeout(updateSoon, 450);
      },
      true
    );

    document.body.addEventListener('click', (e) => {
      const el = e.target.closest(
        '[name="remove"],[data-ajax-cart-remove],[data-cart-remove],.js-qty__adjust,.quantity__button,.cart-remove-button'
      );
      if (el) {
        setTimeout(updateSoon, 120);
        setTimeout(updateSoon, 500);
      }
    });

    document.body.addEventListener('change', (e) => {
      const q = e.target.closest(
        'input[name^="updates"], .quantity__input, input[name="quantity"]'
      );
      if (q) {
        setTimeout(updateSoon, 120);
        setTimeout(updateSoon, 500);
      }
    });

    window.addEventListener('message', (ev) => {
      if (!ev || !ev.data) return;
      const t = ev.data.type || ev.data.event;
      const s = ('' + t).toLowerCase();
      if (s.includes('rebuy') && s.includes('cart')) updateSoon();
    });

    document.addEventListener('rebuy:cart.change', updateSoon);
    document.addEventListener('rebuy:cart.add', updateSoon);
    document.addEventListener('rebuy:cart.remove', updateSoon);

    const drawer = document.querySelector('.cart-drawer, #CartDrawer, [data-cart-drawer]');
    if (drawer) {
      const mo = new MutationObserver(updateSoon);
      mo.observe(drawer, { childList: true, subtree: true });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', updateNow);
    } else {
      updateNow();
    }
  }

  window.StickyBar = window.StickyBar || {};
  if (shouldRunThreshold) {
    window.StickyBar.updateThreshold = updateNow;
    // Legacy aliases, remove once snippets/cart-drawer.liquid is updated.
    window.BFCM = window.StickyBar;
    window.ANNIVERSARY = window.StickyBar;
  }

  function initBar() {
    mountBottomBar();
    initCountdown();
    wireEvents();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBar);
  } else {
    initBar();
  }
})();
